import { ChevronDown } from '@tamagui/lucide-icons';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { Group } from '@unistocks/components/group/group';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { formatCurrency, formatNumber, formatPercentage, t } from '@unistocks/localization';
import {
  ExternalAccountDto,
  ExternalPortfolioDto,
  InstrumentDto
} from '@unistocks/proxy/axios-client';
import { assetTypeHasTotalValue } from '@unistocks/shared/utils/asset-utils';
import { isNumber } from '@unistocks/shared/utils/util';
import { memo } from 'react';
import { ListItem, Separator, Square } from 'tamagui';

export type InstrumentDetailsProps = {
  instrument: InstrumentDto;
  externalAccount: ExternalAccountDto;
  portfolio: ExternalPortfolioDto;
};

export const InstrumentDetails = memo<InstrumentDetailsProps>(
  ({ instrument, externalAccount, portfolio }) => {
    const icon = externalAccount.provider.imageUri ? (
      <Image rounded size={'$2.5'} source={externalAccount.provider.imageUri} />
    ) : (
      <UnistocksCircle rounded size={'$2.5'} />
    );

    return (
      <Accordion collapsible>
        <Accordion.Item value={'asset-details'}>
          <Accordion.Trigger>
            {({ open }: { open: boolean }) => (
              <ListItem
                gap={'$3'}
                title={instrument.name}
                subTitle={t('::Import:ShowInstrumentDetails')}
                icon={icon}
                iconAfter={
                  <Square transition={'quickLessBouncy'} rotate={open ? '180deg' : '0deg'}>
                    <ChevronDown size={'$2'} />
                  </Square>
                }
              />
            )}
          </Accordion.Trigger>
          <Accordion.Content>
            <Group separator={<Separator />}>
              <Group.Item>
                <ListItem
                  title={t('::AccountName')}
                  subTitle={portfolio.name}
                  icon={icon}
                  gap={'$3'}
                />
              </Group.Item>

              {isNumber(instrument.quantity) ? (
                <Group.Item>
                  <ListItem
                    title={t('::Quantity')}
                    subTitle={`${formatNumber(instrument.quantity)} ${t('::QuantitySuffix')}`}
                    icon={icon}
                    gap={'$3'}
                  />
                </Group.Item>
              ) : null}

              {isNumber(instrument.averageAcquisitionPrice) ? (
                <Group.Item>
                  <ListItem
                    title={t('::AverageAcquisitionPrice')}
                    subTitle={`${formatCurrency(
                      instrument.averageAcquisitionPrice,
                      portfolio.currency
                    )}`}
                    icon={icon}
                    gap={'$3'}
                  />
                </Group.Item>
              ) : null}

              {isNumber(instrument.averageAcquisitionPriceAssetCurrency) &&
              instrument.currency !== portfolio.currency ? (
                <Group.Item>
                  <ListItem
                    title={t('::AverageAcquisitionPrice') + ` (${instrument.currency})`}
                    subTitle={`${formatCurrency(
                      instrument.averageAcquisitionPriceAssetCurrency,
                      instrument.currency
                    )}`}
                    icon={icon}
                    gap={'$3'}
                  />
                </Group.Item>
              ) : null}

              {isNumber(instrument.interestRate) ? (
                <Group.Item>
                  <ListItem
                    title={t('::InterestRate')}
                    subTitle={formatPercentage(instrument.interestRate)}
                    icon={icon}
                    gap={'$3'}
                  />
                </Group.Item>
              ) : null}

              {isNumber(instrument.totalValue) && assetTypeHasTotalValue(instrument.assetType) ? (
                <Group.Item>
                  <ListItem
                    title={t('::TotalValue')}
                    subTitle={formatCurrency(instrument.totalValue, portfolio.currency)}
                    icon={icon}
                    gap={'$3'}
                  />
                </Group.Item>
              ) : null}
            </Group>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    );
  }
);

InstrumentDetails.displayName = 'InstrumentDetails';
